import styled from "styled-components";
import { Controller, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Field } from "../../../common/components/controls/field";
import { Documents } from "../../../common/components/controls/select/documents";
import { DatePickerStateControl } from "../../../common/components/controls/date_picker";
import { Gender } from "../../../common/components/controls/select/gender";
import { CheckBox } from "../../../common/components/controls/checkbox";
import { InfoText } from "../../../common/components/info_text";
import Select from "../../../common/components/form-control/select";
import AddressInformation from "../../../common/components/address-information";
import { textChooser } from "../../../common/util/textChooser";
import { InformationForm } from "../../../common/constants/app.dto";
import { IUserState } from "../../../context/states/userState";
import { ICountry, useCountries } from "../../../common/hooks/useCountries";

const LabelWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
`;

interface FormContent {
  hasError: boolean;
  geo: any;
  setGeo: (geo: any) => void;
  register: UseFormRegister<InformationForm>
  control?: any
  setValue: UseFormSetValue<InformationForm>
  errors: any;
  userData: IUserState
}

export default function InformationFormContent({ hasError, geo, setGeo, register, control, setValue, errors, userData }: FormContent) {
  
  const { data, isLoading } = useCountries();

  const nationalityOptions = data && data.map((item: ICountry) => ({ 
    id: item.idCountry, label: item.nacionalidad 
  }));

  return (
    <>
      <div className="row">
        <div className="col-sm-12 col-md-6">
          <Field
            type="text"
            maxLength={65}
            label={textChooser("txt22")}
            hooks={{ ...register("name", { required: true }) }}
            placeholder={"John"}
            defaultValue={userData.name}
            hasError={!!errors.name}
            hasIcon
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <Field
            type="text"
            maxLength={40}
            label={textChooser("txt23")}
            hooks={{ ...register("lastName", { required: true }) }}
            placeholder={"Smith"}
            defaultValue={userData.lastName}
            hasError={!!errors.lastName}
            hasIcon
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-6">
          <Field
            type="email"
            maxLength={35}
            hasError={false}
            hasIcon={false}
            label={textChooser("txt4")}
            hooks={{ ...register("email") }}
            placeholder={"john@smith.com"}
            defaultValue={userData.email}
            disabled={true}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <Field
            type="tel"
            maxLength={18}
            hasError={false}
            hasIcon={false}
            hooks={{ ...register("cellPhone", { required: true }) }}
            placeholder={"999 999 999"}
            label={textChooser("txt32")}
            defaultValue={userData.cellPhone}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-6">
          <Controller
            name="nationality"
            defaultValue={userData.nationality}
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Select {...field} options={nationalityOptions} label={textChooser("txt71")} disabled={isLoading} />}
          />
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-sm-12 col-md-6">
          <Documents
            hook={{ ...register("documentType", { required: true }) }}
            hasError={!!errors.documentType}
            hasIcon
            defaultValue={userData.documentType}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <Field
            type="tel"
            maxLength={10}
            subLabel={textChooser("txt74")}
            hooks={{ ...register("documentValue", { required: true }) }}
            defaultValue={userData.documentValue}
            hasError={!!errors.documentValue}
            hasIcon
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-6">
          {userData && (
            <DatePickerStateControl
              subLabel={textChooser("txt93")}
              selected={userData.birthAt}
              onChange={(date) => { setValue("birthAt", date) }}
              minAgeRequired
            />
          )}
        </div>
        <div className="col-sm-12 col-md-6">
          <Gender
            hook={{ ...register("gender", { required: true }) }}
            hasError={!!errors.gender}
            hasIcon
            defaultValue={userData.gender}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <b>Lugar de residencia:</b>
        </div>
      </div>
      <AddressInformation control={control} address={userData.addresses[0]} />
      <hr />
      <div className="row">
        <div className="col-sm-12">
          <p>{textChooser("txt116")}</p>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-2">
          <LabelWrapper htmlFor="secondaryStudyTrue">
            <input
              {...register("secondaryStudy", { required: true })}
              id="secondaryStudyTrue"
              defaultChecked={userData.secondaryStudy}
              type="radio"
              value="true"
            />
            <span className="text sizeSm">
              {textChooser("txt111")}
            </span>
          </LabelWrapper>
        </div>
        <div className="col-xs-2">
          <LabelWrapper htmlFor="secondaryStudyFalse">
            <input
              {...register("secondaryStudy", { required: true })}
              id="secondaryStudyFalse"
              defaultChecked={!userData.secondaryStudy}
              type="radio"
              value="false"
            />
            <span className="text sizeSm">
              {textChooser("txt112")}
            </span>
          </LabelWrapper>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-sm-12 col-md-6">
          <Field
            type="tel"
            maxLength={8}
            label={textChooser("txt75")}
            subLabel={textChooser("txt76")}
            hooks={{ ...register("salaryRange", { required: true }) }}
            placeholder="2200"
            defaultValue={userData.salaryRange}
            hasError={!!errors.salaryRange}
            hasIcon
          />
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-sm-12">
          <CheckBox
            hook={{ ...register("hasImmediateAvailability") }}
            label={textChooser("txt87")}
            name={"hasImmediateAvailability"}
            className={"primary"}
            defaultValue={userData.hasImmediateAvailability}
          />
          <CheckBox
            hook={{ ...register("hasWorkEquipment") }}
            label={textChooser("txt88")}
            name={"hasWorkEquipment"}
            className={"primary"}
            defaultValue={userData.hasWorkEquipment}
          />
          <CheckBox
            hook={{ ...register("hasImmediateTravel") }}
            name={"hasImmediateTravel"}
            label={textChooser("txt89")}
            className={"primary"}
            defaultValue={userData.hasImmediateTravel}
          />
          <CheckBox
            hook={{ ...register("mobility") }}
            name={"mobility"}
            label={textChooser("txt214")}
            className={"primary"}
            defaultValue={userData.mobility}
          />
          <CheckBox
            hook={{ ...register("hasDriverLicense") }}
            name={"hasDriverLicense"}
            label={textChooser("txt218")}
            className={"primary"}
            defaultValue={userData.hasDriverLicense}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12" hidden={!hasError}>
          <InfoText text={textChooser("txt141")} type="danger" />
        </div>
      </div>
    </>
  );
}